/**
 * Developers · Reveal API key sheet — re-skin of the DevelopersSection
 * "your new key" view. Shows the freshly created secret once, with a copy
 * action and a clear "you won't see it again" warning. Side Sheet only.
 */
import { useState } from "react";
import { Check, Copy, ShieldAlert, X } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import type { ApiKeyWithSecret } from "@/types/api-keys";

interface DevelopersRevealKeySheetProps {
  apiKey: ApiKeyWithSecret | null;
  onClose: () => void;
}

const MONO =
  "ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, 'Liberation Mono', monospace";

export const DevelopersRevealKeySheet = ({
  apiKey,
  onClose,
}: DevelopersRevealKeySheetProps) => {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    if (!apiKey) return;
    try {
      await navigator.clipboard.writeText(apiKey.key);
      setCopied(true);
      toast.success("API key copied to clipboard.");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("We couldn't copy that. Please copy it manually.");
    }
  };

  return (
    <Sheet open={!!apiKey} onOpenChange={(o) => !o && onClose()}>
      <SheetContent
        side="right"
        showClose={false}
        className="w-full sm:max-w-md flex flex-col h-full p-0 bg-genie-background"
      >
        {/* header */}
        <div className="h-14 flex-shrink-0 px-6 bg-white border-b border-genie-border flex items-center justify-between">
          <h3 className="text-[17px] font-extrabold text-[#222B45] tracking-[-0.3px]">
            Your new API key
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full inline-flex items-center justify-center text-gray-400 hover:bg-gray-100 hover:text-[#222B45] transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* body */}
        <div className="flex-1 overflow-auto px-6 py-6 flex flex-col gap-4">
          {/* one-time warning */}
          <div className="flex items-start gap-2.5 px-4 py-3 rounded-[14px] bg-genie-secondary/[0.06] border border-genie-secondary/25">
            <ShieldAlert className="w-[18px] h-[18px] text-genie-secondary flex-shrink-0 mt-0.5" />
            <p className="text-[13px] text-genie-secondary leading-relaxed font-semibold">
              Copy your key now. For your security, you won't be able to see it
              again.
            </p>
          </div>

          {/* key display */}
          <div className="rounded-[18px] border border-genie-border bg-white shadow-sm px-5 py-5">
            <p className="text-[12.5px] font-semibold text-[#64748B] mb-2">
              {apiKey?.name}
            </p>
            <div className="flex items-stretch gap-2">
              <div
                className="flex-1 min-w-0 px-3.5 py-3 rounded-xl bg-genie-background border border-genie-border text-[13px] text-[#222B45] break-all select-all"
                style={{ fontFamily: MONO }}
              >
                {apiKey?.key}
              </div>
              <button
                type="button"
                onClick={copy}
                title="Copy key"
                className="w-11 flex-shrink-0 rounded-xl border border-genie-border inline-flex items-center justify-center text-[#64748B] hover:border-genie-primary hover:text-genie-primary transition-colors"
              >
                {copied ? (
                  <Check className="w-4 h-4 text-genie-success" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>

          {/* usage hint */}
          <div className="rounded-[18px] border border-genie-border bg-white shadow-sm px-5 py-5">
            <p className="text-[12.5px] font-semibold text-[#64748B] mb-2">
              Authenticate your requests
            </p>
            <div
              className="px-3.5 py-3 rounded-xl bg-[#222B45] text-[12px] text-gray-300 overflow-x-auto"
              style={{ fontFamily: MONO }}
            >
              <span className="text-gray-500">Authorization:</span> Bearer{" "}
              {apiKey?.key?.slice(0, 20)}...
            </div>
          </div>
        </div>

        {/* footer */}
        <div className="flex-shrink-0 px-6 py-4 bg-white border-t border-genie-border">
          <button
            type="button"
            onClick={onClose}
            className="w-full h-11 rounded-full inline-flex items-center justify-center text-[14px] font-bold text-white bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] shadow-lg shadow-genie-primary/30 transition-all hover:opacity-90"
          >
            Done
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default DevelopersRevealKeySheet;
